import { type ReactElement } from 'react';
import { useViewport } from '@/lib/hooks/use-viewport';
import {
  useMasteryLevels,
  useQueueStatuses,
  useKeys,
  useTimeSignatures,
} from '@/lib/queries/reference-data';
import { useAddToRepertoire } from '@/lib/queries/pieces';
import { IconButton } from '@/components/atoms/icon-button';
import { Micro } from '@/components/atoms/micro';
import {
  RepertoireEntryForm,
  type RepertoireEntryFormData,
} from '@/components/organisms/repertoire-entry-form';
import type { Arrangement } from '@/lib/types';

export type AddToRepertoireSheetArrangement = {
  arrangementId: string;
  prettyName: string;
  keyName?: string | null;
  formName?: string | null;
};

export type AddToRepertoireSheetProps = {
  open: boolean;
  pieceName: string;
  arrangement: AddToRepertoireSheetArrangement;
  onClose: () => void;
  onAdded?: () => void;
};

function toArrangement(
  pieceName: string,
  arrangement: AddToRepertoireSheetArrangement,
): Arrangement {
  return {
    arrangementId: arrangement.arrangementId,
    pieceId: '',
    pieceName,
    prettyName: arrangement.prettyName,
    canonicalName: arrangement.prettyName,
    arrangerId: null,
    arrangerName: null,
    keyId: null,
    keyName: arrangement.keyName ?? null,
    timeSignatureId: null,
    timeSignatureName: null,
    formId: null,
    formName: arrangement.formName ?? null,
  };
}

export function AddToRepertoireSheet({
  open,
  pieceName,
  arrangement,
  onClose,
  onAdded,
}: AddToRepertoireSheetProps): ReactElement | null {
  const viewport = useViewport();
  const masteryLevelsQ = useMasteryLevels();
  const queueStatusesQ = useQueueStatuses();
  const keysQ = useKeys();
  const timeSignaturesQ = useTimeSignatures();
  const addM = useAddToRepertoire();

  if (!open) return null;
  if (!masteryLevelsQ.data || !queueStatusesQ.data) return null;

  const masteryLevels = masteryLevelsQ.data.content ?? [];
  const queueStatuses = queueStatusesQ.data.content ?? [];
  const keys = keysQ.data?.content ?? [];
  const timeSignatures = timeSignaturesQ.data?.content ?? [];

  const formArrangement = toArrangement(pieceName, arrangement);

  const handleSave = async (data: RepertoireEntryFormData): Promise<void> => {
    if (!data.masteryLevelId) return;
    await addM.mutateAsync({
      arrangementId: arrangement.arrangementId,
      masteryLevelId: data.masteryLevelId,
      queueStatusId: data.queueStatusId,
      keyId: data.keyId ?? undefined,
      practiceTempoBeats: data.practiceTempoBeats ?? undefined,
      performanceTempoBeats: data.performanceTempoBeats ?? undefined,
      firstPracticeOn: data.firstPracticeOn ?? null,
    });
    onAdded?.();
    onClose();
  };

  const body: ReactElement = (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-5 pt-5 pb-4 border-b border-[var(--rule)] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Micro>Add to repertoire</Micro>
          <div className="text-[11px] text-[var(--ink-3)] mt-1 truncate">
            {arrangement.prettyName}
            {arrangement.keyName && ` · ${arrangement.keyName}`}
            {arrangement.formName && ` · ${arrangement.formName}`}
          </div>
        </div>
        <IconButton name="x" size={32} onClick={onClose} aria-label="Close" />
      </div>

      <div className="px-5 py-4 overflow-y-auto nice-scroll flex-1 min-h-0">
        <RepertoireEntryForm
          mode="create"
          variant="sheet"
          arrangements={[formArrangement]}
          selectedArrangementId={arrangement.arrangementId}
          masteryLevels={masteryLevels}
          queueStatuses={queueStatuses}
          keys={keys}
          timeSignatures={timeSignatures}
          onSave={(data) => {
            void handleSave(data);
          }}
          onCancel={onClose}
          isSaving={addM.isPending}
          showDangerZone={false}
          showSharedMetadata={false}
        />

        {addM.error && (
          <div role="alert" className="mt-4 text-[12px] text-[var(--err)]">
            {addM.error.message}
          </div>
        )}
      </div>
    </div>
  );

  if (viewport === 'mobile') {
    return (
      <div data-sheet-mode="mobile" className="fixed inset-0 z-50">
        <div className="absolute inset-0 bg-[var(--ink)]/40" onClick={onClose} aria-hidden />
        <div
          className="absolute bottom-0 left-0 right-0 bg-[var(--bg-page)] rounded-t-[var(--r-lg)] shadow-[var(--shadow-2)]"
          style={{ maxHeight: '88vh' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex justify-center pt-2 pb-1" aria-hidden>
            <div className="w-10 h-1 rounded-full bg-[var(--ink-4)]" />
          </div>
          {body}
        </div>
      </div>
    );
  }

  if (viewport === 'tablet') {
    return (
      <div data-sheet-mode="tablet" className="fixed inset-0 z-50 flex items-center justify-center p-6">
        <div className="absolute inset-0 bg-[var(--ink)]/40" onClick={onClose} aria-hidden />
        <div
          className="relative w-[480px] max-h-[88vh] bg-[var(--bg-page)] rounded-[var(--r-lg)] shadow-[var(--shadow-2)] overflow-hidden"
          role="dialog"
          aria-modal="true"
        >
          {body}
        </div>
      </div>
    );
  }

  return (
    <div
      data-sheet-mode="desktop"
      className="fixed inset-y-0 right-0 z-50 w-[480px] bg-[var(--bg-page)] border-l border-[var(--rule)] shadow-[var(--shadow-2)]"
      role="dialog"
      aria-modal="false"
    >
      {body}
    </div>
  );
}
